import { useState } from 'react';
import PropTypes from 'prop-types';
import FileDropzone from '../common/fileUpload/FileDropzone';
import {
  ALLOWED_FILE_TYPES,
  formatFileSize,
} from '../../helpers/fileValidationUtils';
import { FILE_SIZE_LIMITS } from '../../constants';

const MAX_SUBCONTRACTOR_NAME_LENGTH = 255;

const FileIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </svg>
);

const CrossIcon = ({ size = 15 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

CrossIcon.propTypes = {
  size: PropTypes.number,
};

const CheckIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 12l5 5L20 7" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const UploadIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 16V5" />
    <path d="M6 10l6-6 6 6" />
    <path d="M4 20h16" />
  </svg>
);

const dropHint = `PDF up to ${formatFileSize(FILE_SIZE_LIMITS.PDF)} · XLSX up to ${formatFileSize(FILE_SIZE_LIMITS.EXCEL)} · CSV up to ${formatFileSize(FILE_SIZE_LIMITS.CSV)}`;

const SubcontractorQuoteCard = ({
  subcontractor,
  index,
  onNameChange,
  onRemove,
  onFileRemove,
  onDropAccepted,
  onDropRejected,
  dropDisabled,
  dropError,
  autoFocusName,
  status = 'empty',
}) => {
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const { id, name, files } = subcontractor;
  const hasFiles = Array.isArray(files) && files.length > 0;
  const position = index + 1;
  const displayName = name.trim() || `Subcontractor ${position}`;

  const handleRemoveClick = () => {
    if (hasFiles) {
      setConfirmingRemove(true);
    } else {
      onRemove(id);
    }
  };

  // Untouched cards stay neutral; started-but-blocked cards get the amber
  // accent, complete ones the teal accent
  const cardStateClass =
    status === 'ready' ? 'ready' : status === 'empty' ? '' : 'attention';

  const statusChip =
    status === 'ready' ? (
      <span className="um-chip tone-success">
        <CheckIcon />
        {files.length} {files.length === 1 ? 'file' : 'files'}
      </span>
    ) : status === 'needs-name' ? (
      <span className="um-chip tone-warning">Needs company name</span>
    ) : status === 'needs-files' ? (
      <span className="um-chip tone-warning">Needs quote files</span>
    ) : status === 'duplicate-name' ? (
      <span className="um-chip tone-warning">Duplicate name</span>
    ) : status === 'file-errors' ? (
      <span className="um-chip tone-danger">File issue</span>
    ) : null;

  return (
    <div className={`um-sub-card ${cardStateClass}`}>
      <div className="um-sub-overline-row">
        <p className="um-sub-overline">Subcontractor {position}</p>
        {statusChip}
        <button
          type="button"
          className="um-icon-btn"
          aria-label={`Remove ${displayName}`}
          onClick={handleRemoveClick}
        >
          <CrossIcon />
        </button>
      </div>

      <div className="um-field um-sub-name-field">
        <input
          type="text"
          className="um-input"
          placeholder="Company name"
          aria-label={`Subcontractor ${position} company name`}
          value={name}
          maxLength={MAX_SUBCONTRACTOR_NAME_LENGTH}
          autoFocus={autoFocusName}
          onChange={(e) => onNameChange(id, e.target.value)}
        />
      </div>

      {confirmingRemove ? (
        <div className="um-remove-confirm" role="alert">
          <span className="um-remove-confirm-text">
            Remove {displayName} and its {files.length}{' '}
            {files.length === 1 ? 'file' : 'files'}?
          </span>
          <button
            type="button"
            className="um-btn um-btn-ghost"
            onClick={() => setConfirmingRemove(false)}
          >
            Keep
          </button>
          <button
            type="button"
            className="um-btn um-btn-danger"
            onClick={() => onRemove(id)}
          >
            Remove
          </button>
        </div>
      ) : hasFiles ? (
        <>
          <div className="um-file-list">
            {files.map((file) => (
              <div key={file.id}>
                <div className="um-file-row">
                  <span className="um-file-icon">
                    <FileIcon />
                  </span>
                  <span className="um-file-name" title={file.file.path}>
                    {file.file.path}
                  </span>
                  <span className="um-file-size">
                    {formatFileSize(file.rawFile?.size || 0)}
                  </span>
                  <button
                    type="button"
                    className="um-icon-btn"
                    aria-label={`Remove file ${file.file.path}`}
                    onClick={() => onFileRemove(id, file.id)}
                  >
                    <CrossIcon size={14} />
                  </button>
                </div>
                {Array.isArray(file.errors) && file.errors.length > 0 && (
                  <div className="um-file-error-row">
                    <p className="um-file-error">{file.errors.join(', ')}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          <FileDropzone
            onDrop={(accepted) => onDropAccepted(id, accepted)}
            onDropRejected={(rejections) => onDropRejected(id, rejections)}
            accept={ALLOWED_FILE_TYPES}
            multiple={true}
            maxFiles={0}
            disabled={dropDisabled}
            baseClassName="um-add-file"
            additionalClasses={{ [dropDisabled]: 'disabled' }}
            ariaLabel={`Add quote files for ${displayName}`}
          >
            {() => (
              <>
                <PlusIcon />
                Add another file
              </>
            )}
          </FileDropzone>
        </>
      ) : (
        <FileDropzone
          onDrop={(accepted) => onDropAccepted(id, accepted)}
          onDropRejected={(rejections) => onDropRejected(id, rejections)}
          accept={ALLOWED_FILE_TYPES}
          multiple={true}
          maxFiles={0}
          disabled={dropDisabled}
          baseClassName="um-drop"
          additionalClasses={{ [dropDisabled]: 'disabled' }}
          ariaLabel={`Drop quote files for ${displayName}`}
        >
          {({ isDragActive }) => (
            <>
              <UploadIcon />
              <span className="um-drop-main">
                {isDragActive ? (
                  'Drop the files here…'
                ) : (
                  <>
                    Drop this subcontractor&apos;s quote files here, or{' '}
                    <span className="um-browse">browse</span>
                  </>
                )}
              </span>
              <span className="um-drop-hint">{dropHint}</span>
            </>
          )}
        </FileDropzone>
      )}

      {dropError && (
        <p className="um-drop-reject" role="alert">
          {dropError}
        </p>
      )}
    </div>
  );
};

SubcontractorQuoteCard.propTypes = {
  subcontractor: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    files: PropTypes.array.isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onNameChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onFileRemove: PropTypes.func.isRequired,
  onDropAccepted: PropTypes.func.isRequired,
  onDropRejected: PropTypes.func.isRequired,
  dropDisabled: PropTypes.bool,
  dropError: PropTypes.string,
  autoFocusName: PropTypes.bool,
  status: PropTypes.oneOf([
    'empty',
    'needs-name',
    'needs-files',
    'duplicate-name',
    'file-errors',
    'ready',
  ]),
};

export default SubcontractorQuoteCard;
